import type { Messages } from "./en"

export const ru: Messages = {
  common: {
    loading: "Загрузка…",
    createAgent: "Создать агента",
    loadAgentsFailed: "Не удалось загрузить агентов.",
    toolCount_one: "{count} инструмент",
    toolCount_few: "{count} инструмента",
    toolCount_many: "{count} инструментов",
    toolCount_other: "{count} инструмента",
    enabled: "Включено",
    disconnect: "Отключить",
    connectedAs: "Подключено как",
    disconnectFailed: "Не удалось отключить.",
    cancel: "Отмена",
    contactAdmin: "Связаться с админом",
    errorTitle: "Что-то пошло не так",
    errorBody: "В приложении произошла непредвиденная ошибка. Обновите страницу, а если это повторяется — свяжитесь с админом.",
    errorReload: "Обновить",
    name: "Имя",
    actionFailed: "Не получилось.",
    expandAll: "Развернуть всё",
    collapseAll: "Свернуть всё",
    showLess: "свернуть",
    showAllChars: "показать все {count} симв.",
    itemCount_one: "{count} элемент",
    itemCount_few: "{count} элемента",
    itemCount_many: "{count} элементов",
    itemCount_other: "{count} элемента",
    keyCount_one: "{count} ключ",
    keyCount_few: "{count} ключа",
    keyCount_many: "{count} ключей",
    keyCount_other: "{count} ключа",
    status: {
      disabled: "Отключено",
      run: {
        queued: "В очереди",
        running: "Выполняется",
        succeeded: "Успешно",
        failed: "Ошибка",
        cancelled: "Отменено",
        maxIterations: "Достигнут лимит шагов",
        timeout: "Истекло время",
        abandoned: "Заброшено",
      },
      connection: {
        connected: "Подключено",
        unreachable: "Недоступен",
        notChecked: "Не проверено",
      },
    },
  },
  nav: {
    guide: "Руководство",
    chat: "Чат",
    agents: "Агенты",
    toolServers: "Серверы инструментов",
    plugins: "Плагины",
    schedules: "Расписания",
    runs: "Запуски",
    activity: "Активность",
    workspace: "Рабочее пространство",
    billing: "Биллинг",
    admin: "Админ",
    theme: "Тема",
    language: "Язык",
    signOut: "Выйти",
    openMenu: "Открыть меню",
    closeMenu: "Закрыть меню",
  },
  workspace: {
    switcherLabel: "Рабочее пространство",
    loadFailed: "Не удалось загрузить рабочие пространства.",
    new: "Новое пространство",
    newLabel: "Создать пространство",
    dialogDescription: "Назовите новое рабочее пространство. После создания вы переключитесь на него.",
    namePlaceholder: "Acme Inc.",
    create: "Создать пространство",
    toastCreated: "Пространство создано.",
    createFailed: "Не удалось создать пространство.",
  },
  guide: {
    title: "Руководство",
    intro:
      "Здесь вы создаёте собственных ИИ-ассистентов — мы называем их **агентами**. Агент общается с вами, а вы решаете, для чего он нужен и какими инструментами ему можно пользоваться. С ним можно говорить здесь, в вебе, или из Telegram, а ещё он может работать по расписанию, пока вас нет.",
    useCases: {
      title: "Что можно делать",
      intro: "Несколько вещей, для которых люди настраивают агентов:",
      usesLabel: "Использует: {name}",
      items: {
        spending: {
          title: "Учёт расходов",
          blurb: "Расскажите, что потратили, и спросите, куда ушли деньги в этом месяце.",
          using: "Плагин Expense Tracker",
        },
        email: {
          title: "Работа с почтой",
          blurb: "Пусть читает, кратко пересказывает и готовит черновики ответов на письма.",
          using: "Плагин Mail",
        },
        calendar: {
          title: "Календарь и напоминания",
          blurb: "Спросите, что сегодня в планах, или добавьте событие, просто описав его.",
          using: "Плагин Google Calendar",
        },
        briefings: {
          title: "Регулярные сводки",
          blurb: "Получайте ежедневную сводку каждое утро — полностью автоматически.",
          using: "Расписания",
        },
        tools: {
          title: "Свои инструменты",
          blurb: "Подключите любой сервер инструментов — агент сможет вызывать его в работе.",
          using: "Серверы инструментов (MCP)",
        },
        telegram: {
          title: "Чат из Telegram",
          blurb: "Пишите агенту с телефона, как в обычном чате Telegram.",
          using: "Telegram",
        },
      },
    },
    createAgent: {
      title: "Создайте первого агента",
      intro:
        "Агент — это всего три вещи: модель (мозг), ваши инструкции (что делать) и инструменты, которыми вы разрешаете пользоваться. Настройка занимает минуту.",
      step1: "Откройте **Агенты** и нажмите **Новый агент**.",
      step2: "Дайте ему имя — например, «Помощник по деньгам» или «Почтовый ассистент».",
      step3:
        "Опишите простыми словами, что он должен делать, например: «Ты помогаешь мне учитывать траты и отвечаешь на вопросы о бюджете».",
      step4: "Включите нужные инструменты (см. ниже) и сохраните.",
      step5: "Откройте **Чат**, выберите агента и поздоровайтесь.",
      cta: "Создать агента",
    },
    tools: {
      title: "Дайте ему инструменты",
      body:
        "Инструменты позволяют агенту действительно *делать*, а не только говорить. Проще всего — **плагины**: готовые инструменты вроде учёта расходов, почты и календаря, которые включаются для агента. Для чего-то своего можно подключить собственный **сервер инструментов**.",
      browsePlugins: "Смотреть плагины",
      connectServer: "Подключить сервер инструментов",
    },
    notion: {
      title: "Подключите Notion",
      intro:
        "Позвольте агенту читать и обновлять ваши страницы и базы данных Notion. Достаточно одного токена — агент видит только те страницы, которыми вы с ним поделились.",
      step1:
        "В Notion откройте **notion.so/profile/integrations** и нажмите **New integration**. Выберите рабочее пространство и дайте интеграции имя — например, «Мой агент».",
      step2:
        "Откройте созданную интеграцию и скопируйте её **Internal Integration Secret** — токен, начинающийся с `ntn_`. Это единственный ключ, который нужен.",
      step3:
        "Там же в Notion откройте каждую страницу или базу, которую агент должен видеть, нажмите меню **⋯** → **Connections** и добавьте свою интеграцию. Подстраницы подключаются автоматически.",
      step4:
        "Здесь откройте вкладку **Плагины** своего агента, включите **Notion** и вставьте токен в панель Notion. Он сразу проверяется и сохраняется в зашифрованном виде.",
      step5:
        "Откройте **Чат** и попробуйте: спросите агента, что на одной из страниц, которыми вы поделились.",
      openAgents: "Мои агенты",
    },
    telegram: {
      title: "Подключите его к Telegram",
      intro:
        "Хотите общаться с агентом с телефона? Заведите ему собственного Telegram-бота. Писать ему смогут только те, кого вы укажете.",
      step1:
        "В Telegram откройте **@BotFather** и отправьте `/newbot`. Следуйте подсказкам, чтобы назвать бота.",
      step2: "BotFather выдаст **токен** — длинную строку вида `12345:ABC…`. Скопируйте её.",
      step3: "Здесь откройте своего агента, найдите раздел **Telegram** и вставьте токен.",
      step4:
        "Добавьте свой **Telegram user id** в список разрешённых, чтобы иметь возможность писать боту. Узнать свой id можно у **@userinfobot** в Telegram — он пришлёт ваш номер.",
      step5: "Сохраните, откройте нового бота в Telegram и напишите ему. Агент ответит.",
      openAgents: "Мои агенты",
    },
    schedule: {
      title: "Запустите по расписанию",
      body:
        "Агенту не обязательно ждать вас. Задайте расписание — и он работает сам: утренняя сводка, недельный отчёт, ночная проверка. Каждый запуск сохраняется, и видно, что именно он делал.",
      cta: "Настроить расписание",
    },
  },
  landing: {
    meta: {
      title: "AI Agents — персональные ИИ-ассистенты с вашими инструментами",
      description:
        "AI Agents — платформа для создания персональных ИИ-ассистентов: описывайте агентов, подключайте инструменты через собственные MCP-серверы, общайтесь с ними в реальном времени и запускайте их по расписанию.",
    },
    signIn: "Войти",
    openApp: "Открыть приложение",
    getStarted: "Начать",
    hero: {
      title: "Владейте своими агентами. Подключайте собственные инструменты.",
      subtitle:
        "AI Agents — платформа для создания персональных ИИ-ассистентов. Вы описываете агентов, подключаете инструменты через MCP-серверы, которые сами регистрируете, общаетесь с ними в реальном времени и запускаете их по расписанию — каждый запуск полностью трассируется.",
    },
    features: {
      ownAgents: {
        title: "Собственные агенты",
        text: "Создавайте агентов со своими системными промптами, моделями и инструментами. Каждый агент принадлежит вам.",
      },
      ownTools: {
        title: "Свои инструменты",
        text: "Регистрируйте удалённые MCP-серверы, которыми управляете вы. Платформа находит их каталоги инструментов; вы выбираете, какие инструменты доступны каждому агенту.",
      },
      liveChat: {
        title: "Живой чат",
        text: "Общайтесь с агентами в режиме стриминга и наблюдайте вызовы инструментов в реальном времени.",
      },
      schedules: {
        title: "Расписания",
        text: "Запускайте агентов без вашего участия по cron-расписанию — отчёты, проверки и рутинные задачи, пока вы спите.",
      },
      traces: {
        title: "Полные трассы",
        text: "Каждый запуск записывается: итерации, расход токенов и полная трасса каждого вызова инструмента.",
      },
      selfHost: {
        title: "Self-hosted",
        text: "FastAPI и Postgres, больше ничего. Ваши данные и учётные данные остаются на вашем сервере и шифруются при хранении.",
      },
    },
    google: {
      title: "Вход через Google",
      body: "Вы можете создать аккаунт с email и паролем или войти через Google. При входе через Google AI Agents получает только идентификатор вашего аккаунта Google, email и имя — и использует их с одной целью: создать аккаунт и выполнить вход. AI Agents не запрашивает доступ к Gmail, Google Drive, Google Calendar, контактам или другим данным Google и никогда не продаёт и не передаёт ваши данные.",
      privacyBefore: "Прочитайте",
      privacyLink: "политику конфиденциальности",
      privacyAfter: "— там описано всё, что сервис хранит и как это используется.",
    },
    footer: {
      copyright: "© {year} AI Agents",
      privacy: "Политика конфиденциальности",
    },
  },
  login: {
    tagline: "Владейте своими агентами. Подключайте собственные инструменты.",
    tabs: { signIn: "Войти", signUp: "Создать аккаунт" },
    email: "Email",
    password: "Пароль",
    passwordHint: "Не менее 8 символов",
    codeLabel: "Код подтверждения",
    codePlaceholder: "6-значный код",
    resend: "Отправить код ещё раз",
    submit: {
      wait: "Подождите…",
      signIn: "Войти",
      verify: "Подтвердить и создать аккаунт",
      sendCode: "Отправить код",
      signUp: "Создать аккаунт",
    },
    forgot: "Забыли пароль?",
    or: "или",
    toasts: {
      codeSent: "Мы отправили вам 6-значный код подтверждения.",
      codeResent: "Новый код уже в пути.",
      resendFailed: "Не удалось отправить код повторно.",
      googleFailed: "Не удалось войти через Google. Попробуйте ещё раз.",
      genericError: "Что-то пошло не так. Попробуйте ещё раз.",
    },
  },
  acceptInvite: {
    title: "Приглашение в рабочее пространство",
    invitedBy: "Пригласил(а) {who}",
    sentTo: "Это приглашение отправлено на {email}.",
    checking: "Проверяем приглашение…",
    accepting: "Присоединяемся к рабочему пространству…",
    continue: "Продолжить",
    genericError: "Не удалось принять это приглашение.",
  },
  chat: {
    toasts: {
      loadConversationFailed: "Не удалось загрузить этот разговор.",
      connectionDropped: "Соединение прервалось. Обновите страницу, чтобы увидеть, что сохранилось.",
      startRunFailed: "Не удалось запустить выполнение.",
    },
    noAgents: {
      title: "Создайте агента, чтобы начать общение",
      description:
        "Агент — это модель, ваши инструкции и разрешённые инструменты. Чтобы начать разговор, вам нужен хотя бы один агент.",
      newHere: "Здесь впервые?",
      readGuide: "Прочитать руководство",
    },
    pickAgent: "Выберите агента",
    newConversation: "Новый разговор",
    noConversations: "Разговоров пока нет.",
    untitledConversation: "Без названия",
    you: "Вы",
    agentFallback: "Агент",
    thisAgentFallback: "Этот агент",
    fallbackAgentNameLower: "вашего агента",
    emptyMessages: {
      title: "Напишите что-нибудь, чтобы начать",
      withTools_one: "{name} может использовать {count} инструмент, чтобы вам ответить.",
      withTools_few: "{name} может использовать {count} инструмента, чтобы вам ответить.",
      withTools_many: "{name} может использовать {count} инструментов, чтобы вам ответить.",
      withTools_other: "{name} может использовать {count} инструмента, чтобы вам ответить.",
      noTools: "{name} пока не имеет инструментов. Подключите сервер инструментов, чтобы сделать его полезнее.",
    },
    assistantThinking: "Думает…",
    messagePlaceholder: "Сообщение для {name}…",
    messageAriaLabel: "Сообщение для {name}",
    stop: "Стоп",
    send: "Отправить",
    hint: {
      busy: "Агент работает… вы можете продолжать печатать",
      idle: "Enter — отправить · Shift+Enter — новая строка",
    },
    truncated: "обрезано",
    emptyAssistantMessage: "(использовал инструменты, ничего не сказав)",
    reasoning: {
      live: "Думает…",
      label: "Ход мыслей",
    },
    attach: "Прикрепить изображение",
    removeImage: "Удалить изображение",
    imageTooLarge: "Изображение должно быть не больше 10 МБ.",
    tooManyImages: "Можно прикрепить не более 4 изображений к одному сообщению.",
    attachmentUploadFailed: "Не удалось загрузить изображение.",
  },
  agents: {
    title: "Агенты",
    description: "У каждого агента свои инструкции, модель и набор инструментов.",
    newAgent: "Новый агент",
    empty: {
      title: "Пока нет агентов",
      description:
        "Агент — это модель, системный промпт и инструменты, которые вы разрешаете использовать. Создайте одного, чтобы начать общение.",
    },
    noDescription: "Без описания.",
    chatAction: "Чат",
    editAria: "Редактировать {name}",
  },
  editor: {
    toasts: {
      loadFailed: "Не удалось загрузить этого агента.",
      saved: "Сохранено: {name}",
      saveFailed: "Не удалось сохранить этого агента.",
      deleted: "Агент удалён.",
      deleteFailed: "Не удалось удалить этого агента.",
    },
    noProvider: {
      title: "Провайдер модели не настроен",
      description: "Задайте DEEPSEEK_API_KEY (или LLM_PROVIDERS) на сервере и перезапустите его.",
    },
    basics: {
      title: "Основное",
      nameLabel: "Имя",
      namePlaceholder: "Помощник поддержки",
      descriptionLabel: "Описание",
      descriptionPlaceholder: "Для чего этот агент?",
      systemPromptLabel: "Системный промпт",
      systemPromptPlaceholder:
        "Ты полезный ассистент. Используй доступные инструменты, чтобы отвечать на вопросы о клиентах.",
      systemPromptHint:
        "Отправляется в начале каждого разговора и фиксируется в каждом запуске, чтобы прошлые запуски сохраняли свой первоначальный смысл.",
      activeLabel: "Активен — неактивные агенты нельзя запустить",
      webToolsLabel: "Веб-инструменты — позволяют агенту искать в интернете и читать страницы",
      responseStyleLabel: "Стиль ответа",
      responseStyleHint: "Насколько длинными и подробными будут ответы агента.",
      responseStyleConcise: "Кратко",
      responseStyleExtended: "Развёрнуто",
      responseStyleDetailed: "Подробно",
    },
    model: {
      title: "Модель",
      description: "Провайдеров настраивает тот, кто запускает этот сервер.",
      providerLabel: "Провайдер",
      providerPlaceholder: "Выберите провайдера",
      noApiKeySuffix: " (нет API-ключа)",
      modelLabel: "Модель",
      modelPlaceholder: "Выберите модель",
      temperatureLabel: "Температура",
      temperaturePlaceholder: "по умолчанию у провайдера",
      stepLimitLabel: "Лимит шагов",
      stepLimitHint: "Сколько раундов использования инструментов допустимо до завершения работы агента.",
    },
    toolsSection: {
      title: "Инструменты",
      description: "Модели предлагается только то, что вы включите здесь.",
      emptyTitle: "Нет подключённых серверов инструментов",
      emptyDescription: "Сначала подключите MCP-сервер — и его инструменты появятся здесь.",
      goToServers: "Перейти к серверам инструментов",
      availableCount_one: "Доступен {count} инструмент",
      availableCount_few: "Доступно {count} инструмента",
      availableCount_many: "Доступно {count} инструментов",
      availableCount_other: "Доступно {count} инструмента",
      useAllLabel: "Использовать все",
      autoIncludeHint: "Инструменты, добавленные на этот сервер позже, станут доступны автоматически.",
    },
    saveFirst: {
      title: "Плагины, Telegram и память",
      description:
        "Сначала сохраните агента — после этого появятся вкладки «Плагины», «Telegram» и «Память».",
    },
    submit: {
      save: "Сохранить агента",
      saving: "Сохранение…",
    },
    deleteButton: "Удалить",
    deleteConfirm:
      "Удалить {name}? Его память, Telegram-бот и расписания будут удалены. Прошлые запуски останутся на странице «Запуски».",
    title: {
      new: "Новый агент",
      edit: "Редактировать агента",
    },
    description: "Задайте инструкции, выберите модель и решите, какими инструментами он может пользоваться.",
    back: "Назад",
    tabs: {
      settings: "Настройки",
      plugins: "Плагины",
      telegram: "Telegram",
      memory: "Память",
    },
  },
  memory: {
    toasts: {
      loadFailed: "Не удалось загрузить память.",
      updateAgentFailed: "Не удалось обновить агента.",
      saveFailed: "Не удалось сохранить запись.",
      editFailed: "Не удалось обновить запись.",
      deleted: "Запись удалена.",
      deleteFailed: "Не удалось удалить запись.",
    },
    deleteConfirm: "Удалить эту запись без возможности восстановления?",
    loading: "Загрузка памяти…",
    title: "Память",
    savedCount_one: "Сохранён {count} факт.",
    savedCount_few: "Сохранено {count} факта.",
    savedCount_many: "Сохранено {count} фактов.",
    savedCount_other: "Сохранено {count} факта.",
    hintEnabled:
      "Общая для всех чатов с этим агентом, включая Telegram. Новые факты учитываются в каждом запуске.",
    hintDisabled: "Память отключена — агент их не видит и не сохраняет.",
    addPlaceholder: "Добавьте факт, который агент должен запомнить…",
    saveButton: "Сохранить запись",
    searchPlaceholder: "Поиск по памяти…",
    emptyNone: "Записей пока нет. Агент сохраняет факты по ходу разговора, или добавьте запись выше.",
    emptyNoMatch: "Ничего не найдено по запросу «{query}».",
    editSaveButton: "Сохранить",
    editCancelButton: "Отмена",
    editAria: "Редактировать запись",
    deleteAria: "Удалить запись",
  },
  pluginsTab: {
    toasts: {
      loadFailed: "Не удалось загрузить плагины.",
      enabled: "Включено: {name}",
      enableFailed: "Не удалось включить этот плагин.",
      saved: "Сохранено: {name}",
      saveConfigFailed: "Не удалось сохранить этот плагин.",
      disabled: "Отключено: {name}",
      disableFailed: "Не удалось отключить этот плагин.",
      missingRequired: "Обязательно: {fields}",
    },
    loading: "Загрузка плагинов…",
    mailAccountSetupBadge: "Нужна настройка аккаунта",
    notionSetupBadge: "Нужен токен интеграции",
    telegramSetupBadge: "Нужен вход по QR-коду",
    browseTitle: "Плагины",
    searchPlaceholder: "Поиск плагинов…",
    emptyNone: "Плагины недоступны.",
    emptyNoMatch: "Ничего не найдено по запросу «{query}».",
    needsApiKeyBadge: "Нужен API-ключ",
    needsGoogleBadge: "Нужен аккаунт Google",
    removedBadge: "Больше недоступен",
    backButton: "Все плагины",
    skillsTitle: "Навыки",
    noSkills: "У этого плагина нет навыков.",
    mcpServersTitle: "MCP-серверы",
    noExternalServers: "Этот плагин выполняет свои инструменты внутри платформы — внешних серверов нет.",
    connectionErrorTitle: "Ошибка подключения",
    configTitle: "Настройка",
    secretFieldPlaceholder: "Оставьте пустым, чтобы сохранить текущее значение",
    saveConfigButton: "Сохранить настройку",
    disableConfirmOAuth:
      "Отключить этот плагин для агента? Его аккаунт Google будет отсоединён; сохранённые данные останутся.",
    disableConfirmPlain: "Отключить этот плагин для агента? Сохранённые данные останутся.",
    oauth: {
      toasts: {
        connectFailed: "Не удалось начать подключение.",
        disconnected: "Аккаунт Google отключён.",
      },
      unavailableHint:
        "Администратор сервера должен задать GOOGLE_CLIENT_ID, GOOGLE_CLIENT_SECRET и GOOGLE_OAUTH_REDIRECT_URI, чтобы включить подключение Google.",
      title: "Аккаунт Google",
      connectButton: "Подключить {name}",
      reconnectBadge: "Нужно переподключение",
      lostTitle: "Доступ к Google потерян",
      reconnectButton: "Переподключить",
      disconnectConfirm: "Отсоединить {who} от этого агента?",
      accountFallback: "этот аккаунт Google",
      connectPrompt: "Подключите аккаунт Google, чей календарь должен использовать этот агент.",
    },
  },
  telegram: {
    toasts: {
      loadFailed: "Не удалось загрузить настройки Telegram.",
      missingToken: "Сначала вставьте токен бота от @BotFather.",
      connected: "Подключено как @{username}.",
      saveFailed: "Не удалось сохранить.",
      disconnected: "Бот отключён.",
    },
    invalidIds: "Это не ID пользователей: {ids}",
    disconnectConfirm: "Отключить этого Telegram-бота? Список разрешённых тоже будет удалён.",
    title: "Telegram",
    description:
      "Общайтесь с этим агентом из Telegram — напрямую или в группе (упомяните бота или используйте /ask). Отвечает только пользователям из белого списка, откуда бы они ни писали.",
    tokenSuffix: "(токен ••••{last4})",
    steps: {
      step1: "Напишите @BotFather, отправьте /newbot и скопируйте токен.",
      step2: "Вставьте токен ниже и сохраните.",
      step3: "Отправьте /start своему боту — он ответит вашим user ID для белого списка.",
      step4:
        "Для групп: в @BotFather выполните /setprivacy → Disable, затем добавьте бота в группу (добавьте заново, если он уже там был) — иначе Telegram доставляет только /команды, но не @упоминания.",
    },
    errorTitle: "Ошибка бота",
    tokenLabel: "Токен бота",
    tokenPlaceholderKeep: "Оставьте пустым, чтобы сохранить текущий токен",
    userIdsLabel: "Разрешённые ID пользователей",
    userIdsHint:
      "По одному в строке. Пользователь из списка может писать боту откуда угодно — в личку или в любую группу. Тот, кого нет в списке, получит ответ со своим user ID, чтобы вы могли вставить его сюда.",
    submit: {
      save: "Сохранить настройки Telegram",
      connect: "Подключить бота",
    },
  },
  connections: {
    title: "Серверы инструментов",
    description: "Подключите MCP-сервер — и его инструменты станут доступны вашим агентам.",
    addServer: "Добавить сервер",
    empty: {
      title: "Пока нет серверов инструментов",
      description:
        "Агенты могут использовать только те инструменты, которые вы подключили. Добавьте URL MCP-сервера — его инструменты будут обнаружены автоматически.",
      action: "Добавьте первый сервер",
    },
    toasts: {
      loadFailed: "Не удалось загрузить серверы инструментов.",
      reachable: "{name} доступен.",
      catalogUpdated: "Каталог обновлён: добавлено {added}, обновлено {updated}, удалено {removed}.",
      loadToolsFailed: "Не удалось загрузить список инструментов.",
      removed: "{name} удалён.",
      connectFailed: "Не удалось подключиться к этому серверу.",
    },
    connectedCount_one: "Подключено. Найден {count} инструмент.",
    connectedCount_few: "Подключено. Найдено {count} инструмента.",
    connectedCount_many: "Подключено. Найдено {count} инструментов.",
    connectedCount_other: "Подключено. Найдено {count} инструмента.",
    lastAttemptFailedTitle: "Последняя попытка не удалась",
    serverInfo: "Сервер: {name}{version}",
    checkedAt: "Проверено {when}",
    test: "Тест",
    refreshTools: "Обновить инструменты",
    browseTools: "Просмотреть инструменты",
    removeConfirm: "Удалить {name}? Агенты, которые его используют, лишатся этих инструментов.",
    remove: "Удалить",
    noTools: "Этот сервер не предоставляет инструментов.",
    unsupported: "Не поддерживается",
    callsAs: "вызывается как {name}",
    dialog: {
      title: "Добавить сервер инструментов",
      description:
        "Вставьте URL удалённого MCP-сервера. Мы сразу подключимся и покажем список доступных инструментов.",
      namePlaceholder: "Пример инструментов",
      urlLabel: "URL сервера",
      urlHint: "Streamable HTTP или SSE — мы определим, какой протокол использует сервер.",
      authLabel: "Аутентификация",
      authNone: "Нет",
      authBearer: "Bearer-токен",
      authHeader: "Произвольный заголовок",
      authBasic: "Basic (user:password)",
      headerNameLabel: "Имя заголовка",
      secretLabel: "Секрет",
      secretLabelBasic: "username:password",
      secretHint: "Шифруется перед сохранением и больше никогда не показывается.",
      connect: "Подключить",
      connecting: "Подключение…",
    },
  },
  mailAccounts: {
    title: "Почтовые аккаунты",
    description:
      "Добавьте столько почтовых ящиков, сколько нужно этому агенту. Каждый использует пароль приложения, никогда не ваш основной пароль.",
    addAccount: "Добавить аккаунт",
    loading: "Загрузка аккаунтов…",
    empty: "Аккаунтов пока нет. Пока вы не добавите хотя бы один, почтовые инструменты агента будут его запрашивать.",
    access: {
      read: { label: "Чтение", hint: "Поиск и чтение; можно отмечать прочитанным или флагом." },
      send: { label: "Чтение + отправка", hint: "Также можно составлять и отвечать на письма." },
      full: { label: "Полный доступ", hint: "Также можно перемещать и удалять в корзину." },
    },
    status: {
      error: "Ошибка",
    },
    testAria: "Проверить {email}",
    editAria: "Редактировать {email}",
    removeAria: "Удалить {email}",
    toasts: {
      loadFailed: "Не удалось загрузить почтовые аккаунты.",
      connected: "{email} подключён.",
      connectFailed: "Не удалось подключить {email}.",
      testFailed: "Не удалось проверить этот аккаунт.",
      removeFailed: "Не удалось удалить этот аккаунт.",
      saveFailed: "Не удалось сохранить этот аккаунт.",
    },
    removeConfirm: "Удалить {email} из этого агента? Сохранённый пароль будет удалён.",
    dialog: {
      needPassword: "Введите пароль приложения.",
      needServers: "Выберите провайдера или заполните серверы в разделе «Дополнительно».",
      editTitle: "Редактировать {email}",
      addTitle: "Добавить почтовый аккаунт",
      description: "Используйте пароль приложения от вашего провайдера. При сохранении соединение проверяется.",
      providerLabel: "Провайдер",
      providerOther: "Другой (указать серверы)",
      emailLabel: "Email адрес",
      passwordLabel: "Пароль приложения",
      passwordPlaceholderPlain: "Оставьте пустым, чтобы сохранить текущий",
      passwordPlaceholderLast4: "Оставьте пустым, чтобы сохранить текущий (••••{last4})",
      helpLink: "Как создать пароль приложения для {provider}",
      displayNameLabel: "Отображаемое имя",
      labelLabel: "Метка",
      labelPlaceholder: "По адресу, например gmail",
      accessLabel: "Что может делать агент",
      advanced: "Дополнительно",
      imapTitle: "Входящие (IMAP)",
      smtpTitle: "Исходящие (SMTP)",
      hostAria: "{title}: хост",
      portAria: "{title}: порт",
      securityAria: "{title}: защита соединения",
      usernameAria: "{title}: имя пользователя",
      securitySsl: "SSL/TLS",
      securityStarttls: "STARTTLS",
      usernamePlaceholder: "Имя пользователя (по умолчанию — email адрес)",
      sentFolderLabel: "Папка «Отправленные»",
      sentFolderPlaceholder: "Определяется автоматически",
      appendSentLabel: "Сохранять копию отправленных писем в папку «Отправленные»",
      save: "Сохранить",
    },
  },
  notionPanel: {
    title: "Рабочее пространство Notion",
    description:
      "Вставьте токен внутренней интеграции. Агент видит только те страницы, которыми вы поделились с интеграцией в Notion.",
    loading: "Проверяем подключение…",
    tokenLabel: "Токен интеграции",
    connectButton: "Подключить",
    cancelButton: "Отмена",
    replaceButton: "Заменить токен",
    unknownWorkspace: "Рабочее пространство Notion",
    tokenHint: "Токен ••••{last4}",
    status: {
      ok: "Подключено",
      error: "Ошибка",
    },
    testAria: "Проверить подключение",
    removeAria: "Отключить Notion",
    help: "Создайте интеграцию, поделитесь с ней нужными страницами и вставьте её токен:",
    removeConfirm: "Отключить Notion от этого агента? Сохранённый токен будет удалён.",
    toasts: {
      loadFailed: "Не удалось загрузить подключение Notion.",
      connected: "{workspace} подключено.",
      connectFailed: "Не удалось подключить Notion.",
      testOk: "Подключение работает.",
      testFailed: "Не удалось проверить подключение.",
      removeFailed: "Не удалось отключить Notion.",
    },
  },
  telegramPanel: {
    title: "Аккаунт Telegram",
    description:
      "Привяжите свой аккаунт Telegram, отсканировав QR-код. Агент читает и отправляет сообщения от вашего имени — не как бот.",
    loading: "Проверяем подключение…",
    unavailableTitle: "Telegram недоступен",
    unavailableHint:
      "На этом сервере не заданы учётные данные приложения Telegram. Администратор должен задать TELEGRAM_PLUGIN_API_ID и TELEGRAM_PLUGIN_API_HASH.",
    connectButton: "Подключить Telegram",
    reconnectButton: "Переподключить",
    cancelButton: "Отмена",
    unknownAccount: "Аккаунт Telegram",
    qrStep1: "Откройте Telegram на телефоне.",
    qrStep2: "Перейдите в Настройки → Устройства → Подключить устройство.",
    qrStep3: "Наведите камеру телефона на этот код.",
    qrAlt: "QR-код для входа в Telegram",
    passwordLabel: "Пароль двухэтапной проверки",
    passwordHint: "У вашего аккаунта включён пароль 2FA. Введите его, чтобы завершить вход.",
    submitPasswordButton: "Отправить",
    status: {
      ok: "Подключено",
      error: "Ошибка",
    },
    testAria: "Проверить подключение",
    disconnectAria: "Отключить Telegram",
    disconnectConfirm: "Отключить Telegram от этого агента? Сохранённая сессия будет удалена.",
    toasts: {
      loadFailed: "Не удалось загрузить подключение Telegram.",
      startFailed: "Не удалось начать вход в Telegram.",
      loginFailed: "Не удалось войти в Telegram.",
      connected: "Telegram подключён.",
      testOk: "Подключение работает.",
      testFailed: "Не удалось проверить подключение.",
      disconnectFailed: "Не удалось отключить Telegram.",
    },
  },
  plugins: {
    title: "Плагины",
    description: "Наборы навыков и подключённых серверов, которые могут использовать ваши агенты.",
    toasts: {
      loadFailed: "Не удалось загрузить плагины.",
    },
    empty: {
      title: "Плагины недоступны",
      description: "Плагины устанавливаются на сервере. Попросите того, кто управляет этим развёртыванием, добавить нужный.",
    },
    enableHint: "Включайте плагины для каждого агента в редакторе агента.",
    removedBadge: "Удалён",
    needsApiKeyBadge: "Нужен API-ключ",
    needsGoogleBadge: "Нужен аккаунт Google",
    noSkills: "У этого плагина нет навыков.",
  },
  schedules: {
    title: "Расписания",
    description: "Запускайте агента по расписанию или один раз в заданное время — без вашего участия.",
    newSchedule: "Новое расписание",
    empty: {
      title: "Пока нет расписаний",
      noAgents: "Сначала создайте агента, затем вы сможете задать ему расписание.",
      description: "Задайте агенту cron-выражение или дату и промпт — и он будет работать самостоятельно.",
      action: "Создать расписание",
    },
    toasts: {
      loadFailed: "Не удалось загрузить расписания.",
      runFinished: "Запуск завершён со статусом {status}.",
      deleted: "Расписание удалено.",
      created: "Расписание создано.",
      createFailed: "Не удалось создать расписание.",
    },
    missedRunsTitle: "Пропущенные запуски не повторяются",
    missedRunsDescription:
      "Если воркер не работал, самый последний пропущенный слот запускается один раз при восстановлении, а остальные пропускаются. Расписание выполняется не чаще одного раза за слот, поэтому агент, отправляющий почту, не разошлёт накопившиеся дубликаты. Разовые задачи, срок которых наступил за это время, всё равно сработают, хоть и с опозданием.",
    status: {
      completed: "Завершено",
      fired: "Выполнено",
      failed: "Ошибка",
      pending: "Ожидает",
      active: "Активно",
      paused: "Приостановлено",
      pausedReason: "Приостановлено · {reason}",
    },
    onceBadge: "Один раз · {when}",
    nextRunSuffix: " · следующий запуск {when}",
    setInChat: "Задано в чате",
    lastRun: "Последний запуск {when}",
    outcome: "Результат: {status}",
    consecutiveFailures_one: "{count} сбой подряд",
    consecutiveFailures_few: "{count} сбоя подряд",
    consecutiveFailures_many: "{count} сбоев подряд",
    consecutiveFailures_other: "{count} сбоя подряд",
    runNow: "Запустить сейчас",
    deleteConfirm: 'Удалить расписание "{name}"?',
    delete: "Удалить",
    presets: {
      every15: "Каждые 15 минут",
      hourly: "Каждый час",
      weekdayNine: "Каждый будний день в 9:00",
      dailyMidnight: "Ежедневно в полночь",
      mondaysEight: "По понедельникам в 8:00",
    },
    dialog: {
      description: "Агент будет работать без присмотра с промптом ниже.",
      agentLabel: "Агент",
      agentPlaceholder: "Выберите агента",
      namePlaceholderCron: "Ежедневная сводка",
      namePlaceholderOnce: "Вернуть долг Али",
      whenLabel: "Когда",
      recurring: "Повторяющееся",
      once: "Один раз",
      cronAria: "Cron-выражение",
      dateTimeAria: "Дата и время",
      timezoneAria: "Часовой пояс",
      invalidExpression: "Неверное выражение.",
      nextRunsLabel: "Следующие запуски",
      promptLabel: "Промпт",
      promptPlaceholderCron: "Проверь клиентов с более чем 5 открытыми тикетами и составь сводку.",
      promptPlaceholderOnce: "Напомни мне, что Али должен мне 500 000 сум, срок сегодня.",
      create: "Создать расписание",
    },
  },
  runs: {
    title: "Запуски",
    description: {
      admin: "Все запуски агентов на платформе с полной записью вызванных инструментов.",
      user: "Запуски ваших агентов. Полные детали доступны администраторам.",
    },
    filters: {
      allAgents: "Все агенты",
      allStatuses: "Все статусы",
      allTriggers: "Все источники",
      allTelegramChats: "Все чаты Telegram",
    },
    trigger: {
      chat: "Чат",
      schedule: "Расписание",
      telegram: "Telegram",
    },
    empty: {
      title: "Пока нет запусков",
      description: "Напишите агенту или дождитесь срабатывания расписания — история появится здесь.",
    },
    columns: {
      status: "Статус",
      trigger: "Источник",
      telegramChat: "Чат Telegram",
      steps: "Шаги",
      toolCalls: "Вызовы инструментов",
      tokens: "Токены",
      cost: "Стоимость",
      duration: "Длительность",
      started: "Начат",
      created: "Создан",
    },
    tokensTooltip: "входные / выходные токены",
    viewTrace: "Трасса",
    toasts: {
      loadFailed: "Не удалось загрузить запуски.",
    },
    detail: {
      title: "Трасса запуска",
      description: "Точно то, что сделал агент, записанное по мере происходящего.",
      back: "Назад к запускам",
      toasts: {
        loadFailed: "Не удалось загрузить эту трассу.",
      },
      runFailed: "Запуск завершился ошибкой",
      snapshotTitle: "Конфигурация агента на момент запуска",
      snapshotHint:
        "Зафиксировано при старте запуска, поэтому последующие изменения агента не переписывают историю.",
      llmCallsTitle: "Вызовы LLM ({count})",
      noLlmCalls:
        "Запросы не записаны. Запуски, сделанные до появления записи запросов, ничего не показывают.",
      memoryExtraction: "Извлечение памяти",
      stepIndex: "шаг {n}",
      summary: "сводка",
      messageCount_one: "{count} сообщение",
      messageCount_few: "{count} сообщения",
      messageCount_many: "{count} сообщений",
      messageCount_other: "{count} сообщения",
      requestLabel: "Запрос — именно так, как он был отправлен провайдеру",
      responseLabel: "Ответ — именно так, как его вернул провайдер",
      toolCallsTitle: "Вызовы инструментов ({count})",
      noToolCalls: "Этот запуск не использовал инструменты.",
      truncated: "обрезано",
      argumentsLabel: "Аргументы",
      redacted: "(скрыто политикой подключения)",
      resultLabel: "Результат",
      noResult: "(результат не записан)",
      stepsCount_one: "{count} шаг",
      stepsCount_few: "{count} шага",
      stepsCount_many: "{count} шагов",
      stepsCount_other: "{count} шага",
      tokensIn: "{count} входящих",
      tokensOut: "{count} исходящих",
      tokensCached: "{count} из кэша",
      tokensTotal: "{count} всего",
    },
  },
  activity: {
    title: "Активность",
    description: "Недавние действия в вашем аккаунте.",
    toasts: {
      loadFailed: "Не удалось загрузить активность.",
    },
    empty: "Активности пока не зафиксировано.",
    columns: {
      time: "Время",
      action: "Действие",
      target: "Объект",
      ip: "IP",
    },
    changes: {
      redacted: "изменено",
    },
  },
  billing: {
    title: "Биллинг",
    workspaceLabel: "Биллинг для {name}",
    loadFailed: "Не удалось загрузить биллинг",
    yourPlan: "Ваш тариф: {plan}",
    renewsOn: "Продление / сброс: {date}",
    balanceTitle: "Баланс",
    balanceHint: "Запуски и распознавание речи списываются с этого баланса.",
    topUpCta: "Баланс исчерпан. Пополните счёт, чтобы продолжить пользоваться агентами.",
    tokens: "Токены",
    agentsUsage: "Агенты: {used} / {max}",
    free: "Бесплатно",
    tokenCapPeriod_one: "{formatted} токен / 30 дней",
    tokenCapPeriod_few: "{formatted} токена / 30 дней",
    tokenCapPeriod_many: "{formatted} токенов / 30 дней",
    tokenCapPeriod_other: "{formatted} токена / 30 дней",
    planAgents_one: "{count} агент",
    planAgents_few: "{count} агента",
    planAgents_many: "{count} агентов",
    planAgents_other: "{count} агента",
    upgradeTitle: "Как перейти на другой тариф",
    upgradeBefore: "Переведите деньги на карту",
    upgradeMiddle: ", затем напишите",
    upgradeAfter: "для активации тарифа.",
  },
  resetPassword: {
    title: "Сброс пароля",
    description: {
      email: "Введите email, и мы вышлем код.",
      reset: "Введите код и новый пароль.",
    },
    emailLabel: "Email",
    emailPlaceholder: "you@example.com",
    codeLabel: "Код сброса",
    codePlaceholder: "6-значный код",
    newPasswordLabel: "Новый пароль",
    newPasswordPlaceholder: "Не менее 8 символов",
    submit: {
      wait: "Подождите…",
      sendCode: "Отправить код",
      updatePassword: "Обновить пароль",
    },
    backToSignIn: "Назад ко входу",
    toasts: {
      codeSent: "Если такой аккаунт существует, код сброса уже отправлен.",
      passwordUpdated: "Пароль обновлён.",
      genericError: "Что-то пошло не так. Попробуйте ещё раз.",
    },
  },
  oauthCallback: {
    toasts: {
      startAgain: "Начните подключение заново в редакторе агента.",
      cancelled: "Подключение Google отменено.",
      incomplete: "Google не завершил подключение.",
      connected: "Подключено как {email}.",
      connectFailed: "Не удалось подключить Google.",
    },
    yourGoogleAccountFallback: "ваш аккаунт Google",
    connecting: "Подключение Google…",
  },
  workspaceSettings: {
    title: "Настройки рабочего пространства",
    description: "Управляйте участниками, приглашениями и этим рабочим пространством.",
    yourRole: "Ваша роль",
    leave: "Покинуть",
    leaveConfirm: "Покинуть «{name}»? Вы потеряете доступ, если вас не пригласят снова.",
    removeConfirm: "Удалить {email} из этого рабочего пространства?",
    deleteConfirm: "Удалить «{name}»? Это необратимо удалит все данные. Отменить нельзя.",
    lastOwnerHint: "В рабочем пространстве должен остаться хотя бы один владелец.",
    roles: {
      owner: "Владелец",
      admin: "Администратор",
      member: "Участник",
    },
    general: {
      title: "Основное",
      description: "Основная информация об этом рабочем пространстве.",
      save: "Сохранить",
    },
    members: {
      title: "Участники",
      description: "Все, у кого есть доступ к этому рабочему пространству.",
      columnMember: "Участник",
      columnRole: "Роль",
      remove: "Удалить",
    },
    invite: {
      title: "Пригласить",
      description: "Отправьте приглашение по email, чтобы присоединиться к рабочему пространству.",
      emailLabel: "Email",
      roleLabel: "Роль",
      send: "Отправить приглашение",
      empty: "Нет ожидающих приглашений.",
      expires: "Истекает {when}",
      revoke: "Отозвать",
      linkHint: "Или скопируйте ссылку-приглашение (работает, даже если почта не настроена):",
      copy: "Скопировать ссылку",
      copied: "Скопировано",
      resend: "Отправить снова",
    },
    danger: {
      title: "Опасная зона",
      description: "Удаление рабочего пространства необратимо.",
      delete: "Удалить рабочее пространство",
    },
    toasts: {
      loadFailed: "Не удалось загрузить рабочее пространство.",
      membersLoadFailed: "Не удалось загрузить участников.",
      invitesLoadFailed: "Не удалось загрузить приглашения.",
      renamed: "Рабочее пространство переименовано.",
      roleUpdated: "Роль {email} обновлена.",
      left: "Вы покинули рабочее пространство.",
      removed: "{email} удалён из рабочего пространства.",
      inviteSent: "{email} приглашён.",
      inviteResent: "Новая ссылка-приглашение создана для {email}.",
      inviteFailed: "Не удалось отправить приглашение.",
      inviteRevoked: "Приглашение отозвано.",
      deleted: "Рабочее пространство удалено.",
    },
  },
  admin: {
    grant: {
      workspacesTitle: "Рабочие пространства",
      workspacesDescription: "Предоставьте тариф одному из рабочих пространств этого пользователя.",
      columnWorkspace: "Рабочее пространство",
      columnRole: "Роль",
      columnPlan: "Тариф",
      columnRenews: "Продление",
      button: "Предоставить",
      none: "Этот пользователь не состоит ни в одном рабочем пространстве.",
      loadFailed: "Не удалось загрузить рабочие пространства пользователя.",
      granted: "Тариф {plan} предоставлен «{workspace}» на {days} дн.",
      grantFailed: "Не удалось предоставить тариф.",
      daysInvalid: "Количество дней должно быть от 1 до 3650.",
      columnBalance: "Баланс",
      topUpButton: "Пополнить",
      toppedUp: "«{workspace}» пополнен на {amount}.",
      topUpFailed: "Не удалось пополнить баланс.",
      amountInvalid: "Сумма должна быть от $0.01 до $10 000.",
    },
  },
}
